/**
 * Firebase Error Handler
 * Converts Firebase error codes to user-friendly messages
 */

export const getFirebaseErrorMessage = (error) => {
  // Handle user cancellation gracefully (no error message)
  if (error.code === 'auth/popup-closed-by-user' ||
      error.code === 'auth/cancelled-popup-request' ||
      error.code === 'auth/user-cancelled') {
    return null; // Don't show error for user-initiated cancellations
  }

  // Handle network errors
  if (error.code === 'auth/network-request-failed') {
    return 'Network error. Please check your internet connection and try again.';
  }

  // Handle popup blocked by browser
  if (error.code === 'auth/popup-blocked') {
    return 'Sign-in popup was blocked. Please allow popups for this site and try again.';
  }

  // Handle redirect errors (alternative to popup)
  if (error.code === 'auth/redirect-cancelled-by-user') {
    return null; // User cancelled redirect, no error needed
  }

  // Authentication errors
  const errorMessages = {
    // Email/Password Sign In
    'auth/invalid-email': 'Please enter a valid email address.',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/invalid-credential': 'Invalid email or password. Please try again.',
    'auth/too-many-requests': 'Too many failed attempts. Please try again later.',

    // Email/Password Sign Up
    'auth/email-already-in-use': 'An account with this email already exists.',
    'auth/weak-password': 'Password is too weak. Please use at least 6 characters.',
    'auth/operation-not-allowed': 'Email/password accounts are not enabled. Please contact support.',

    // Google Sign In
    'auth/popup-blocked': 'Sign-in popup was blocked. Please allow popups for this site and try again.',
    'auth/account-exists-with-different-credential': 'An account already exists with the same email. Please sign in using your original method.',
    'auth/unauthorized-domain': 'This domain is not authorized for authentication.',
    'auth/auth-domain-config-required': 'Authentication configuration error. Please contact support.',
    'auth/operation-not-supported-in-this-environment': 'Google Sign-in is not supported in this browser. Please try a different browser.',
    'auth/timeout': 'Sign-in timed out. Please try again.',

    // Email Verification
    'auth/invalid-action-code': 'This verification link is invalid or has expired.',
    'auth/expired-action-code': 'This verification link has expired. Please request a new one.',

    // Password Reset
    'auth/missing-email': 'Please enter an email address.',

    // General
    'auth/requires-recent-login': 'Please sign in again to complete this action.',
    'auth/invalid-api-key': 'Authentication service error. Please contact support.',
    'auth/app-deleted': 'Authentication service error. Please contact support.',
    'auth/app-not-authorized': 'Authentication service error. Please contact support.',
  };

  // Return custom message if available, otherwise return a generic message
  return errorMessages[error.code] || 'An unexpected error occurred. Please try again.';
};

/**
 * Check if error should be shown to user
 * Some errors (like popup closed) shouldn't display error messages
 */
export const shouldShowError = (error) => {
  const silentErrors = [
    'auth/popup-closed-by-user',
    'auth/cancelled-popup-request',
    'auth/user-cancelled',
    'auth/redirect-cancelled-by-user',
  ];

  return !silentErrors.includes(error.code);
};
